/**
 * Attractions catalogue — data access layer.
 *
 * Backed by `services/attractions-service` (the Bridgify mirror). The local
 * fixtures this module used to serve are gone; the exported signatures are
 * unchanged, so the pages that consume it did not need rewriting.
 *
 * Layering:
 *   attractions.api.ts  wire shapes + fetch  (the only file that knows the API)
 *   mapper.ts           service record → UI type
 *   booking-shape.ts    funnel-only ticket scaffolding (see its header)
 *   catalogue.ts        this file — the page-facing API
 *
 * When `@wl/api-client` gains an `AttractionsClient`, `attractions.api.ts` is
 * what it replaces; nothing here or above changes.
 */

import {
  EMPTY_FACETS,
  sortOptionFor,
  type AttractionDetail,
  type AttractionFilters,
  type AttractionSummary,
  type ListingFacets,
  type SortValue,
} from '../config';
import {
  fetchAttractionBySlug,
  fetchAttractionByUuid,
  fetchAttractions,
  fetchAvailability,
  fetchBookable,
  fetchCities,
  fetchCityById,
  fetchCityBySlugOrName,
  fetchCitySuggestions,
  fetchPlaceSuggestions,
  type AttractionCityRecord,
  type AttractionPlaceRecord,
  type AttractionSearchParams,
  type CityStatus,
} from './attractions.api';
import { toDetail, toSummary } from './mapper';

/**
 * Results per request.
 *
 * Filtering, sorting and counting all run in the service now, so this is a real
 * page rather than "fetch the whole destination and slice it in the browser".
 * The listing appends the next page on scroll. It was 100 (the service's
 * `@Max(100)` ceiling) while the client did the work — which truncated any
 * destination with more products than that.
 */
export const LISTING_PAGE_SIZE = 24;

export interface SearchAttractionsResult {
  data: AttractionSummary[];
  total: number;
  cityName: string;
  page: number;
  totalPages: number;
  /** Panel contents for page 1; the service omits them on later pages. */
  facets: ListingFacets;
  /** Page 1 of a city search only. */
  cityStatus: CityStatus | null;
}

export interface SearchAttractionsParams {
  /**
   * Canonical Bridgify city id — the AUTHORITATIVE destination filter.
   *
   * When present it is the only destination param that reaches the service, and
   * it is what the facets are built from, so the grid and the filter panel
   * describe the same city. `city` below is then display text only.
   */
  cityId?: number;
  city?: string;
  query?: string;
  filters?: AttractionFilters;
  sort?: SortValue;
  page?: number;
  limit?: number;
}

export async function searchAttractions(
  params: SearchAttractionsParams = {},
): Promise<SearchAttractionsResult> {
  const city = params.city?.trim() ?? '';
  const query = params.query?.trim() ?? '';
  const page = params.page ?? 1;
  const sort = sortOptionFor(params.sort ?? 'recommended');
  const f = params.filters;

  // Only send what the user actually asked for — the service declares `city`
  // and `search`, and an empty value would filter rather than be ignored.
  const search: AttractionSearchParams = {
    limit: params.limit ?? LISTING_PAGE_SIZE,
    page,
    sortBy: sort.sortBy,
    // Every URL-only name is translated to its DTO name HERE, explicitly. The
    // service runs `forbidNonWhitelisted`, so forwarding the page's search
    // params wholesale would 400 the request on the first unrecognised key and
    // the listing would render as "no results" with nothing explaining why.
    ...('sortOrder' in sort ? { sortOrder: sort.sortOrder } : {}),
  };
  /*
   * Destination: the ID when we have one, the NAME only as a fallback.
   *
   * Never both — sending `city` alongside `cityId` would be harmless today
   * (the service prefers the id) but it invites a future reader to treat the
   * name as load-bearing, and the name is exactly the ambiguous key this flow
   * exists to stop relying on.
   */
  if (params.cityId !== undefined) search.cityId = params.cityId;
  else if (city) search.city = city;
  if (query) search.search = query;

  if (f) {
    if (f.types.length) search.types = f.types;
    if (f.features.length) search.features = f.features;
    if (f.languages.length) search.languages = f.languages;
    if (f.durations.length) search.durations = f.durations;
    if (f.startTimes.length) search.startTimes = f.startTimes;
    if (f.guestRatings.length) search.guestRatings = f.guestRatings;
    if (f.minPrice !== null) search.minPrice = f.minPrice;
    if (f.maxPrice !== null) search.maxPrice = f.maxPrice;
  }

  const res = await fetchAttractions(search);

  return {
    data: res.data.map(toSummary),
    // `total` is the count of everything MATCHING the current filters, which is
    // what the "N found" label means — not the length of this page.
    total: res.total,
    // No destination filter → a neutral label. Falling back to the first
    // record's city labelled a mixed, multi-city result set "Milan Attractions".
    cityName: city || 'All',
    page: res.page || page,
    totalPages: res.totalPages,
    facets: res.facets ?? EMPTY_FACETS,
    cityStatus: res.cityStatus ?? null,
  };
}

/** Destinations for the search box / city facet. */
export async function listCities() {
  return fetchCities();
}

/**
 * Resolve a `?cityId=` to the destination we hold for it.
 *
 * `null` means we sell nothing there — NOT "use a different city". The listing
 * page renders that as an explicit no-coverage state.
 */
export async function findCityById(cityId: number) {
  return fetchCityById(cityId);
}

/**
 * Resolve the readable `?city=` value to a destination.
 *
 * `null` means we could not match it — the listing page renders that as a
 * named "we don't cover this" state rather than substituting another city.
 */
export async function findCityBySlugOrName(value: string) {
  return fetchCityBySlugOrName(value);
}

/** Tier-ranked destination suggestions for the hero Location field. */
export async function suggestCities(
  query: string,
  limit: number,
  signal?: AbortSignal,
): Promise<AttractionCityRecord[]> {
  return fetchCitySuggestions(query.trim(), limit, signal);
}

/**
 * Tier-ranked attraction-name suggestions for the hero, scoped to a destination.
 *
 * `scope` is what keeps the dropdown honest: it becomes a `citySlug`/`city`
 * query param, so the service filters in Mongo (`rankedTitleSearch` runs the
 * same `baseMatch` as the listing, destination included) rather than us pulling
 * a global top-5 and discarding rows in the browser — which would show four
 * results for London because the fifth global match happened to be in Paris.
 *
 * With no destination chosen the scope is empty and the search stays catalogue-
 * wide, which is the sensible answer for a visitor who cleared the field.
 */
export async function suggestAttractions(
  query: string,
  limit: number,
  scope: { cityId?: number; city?: string; citySlug?: string } = {},
  signal?: AbortSignal,
): Promise<AttractionSummary[]> {
  const res = await fetchAttractions(
    {
      search: query.trim(),
      titleOnly: true,
      limit,
      // Strictly in order of authority — `cityId`, then the slug, then the
      // display name. The service resolves the same precedence, but sending
      // only the strongest key we hold keeps the request self-describing.
      ...(scope.cityId !== undefined
        ? { cityId: scope.cityId }
        : scope.citySlug
          ? { citySlug: scope.citySlug }
          : scope.city
            ? { city: scope.city }
            : {}),
    },
    signal,
  );
  return res.data.map(toSummary);
}

/**
 * Bridgify POI suggestions for the selected destination.
 *
 * Not wired into the hero — these rows carry no id and so cannot open a product
 * page, which the hero's dropdown does. Exposed for surfaces that want
 * supplier place names alongside our own catalogue.
 */
export async function suggestPlaces(
  query: string,
  limit: number,
  cityId?: number,
  signal?: AbortSignal,
): Promise<AttractionPlaceRecord[]> {
  return fetchPlaceSuggestions(query.trim(), cityId, limit, signal);
}

export async function getAttraction(slug: string): Promise<AttractionDetail | null> {
  const record = await fetchAttractionBySlug(slug);
  return record ? toDetail(record) : null;
}

/**
 * By product id (the Bridgify `uuid`). The service falls back to a live product
 * fetch when the record has not been synced yet, so a brand-new product still
 * resolves.
 */
export async function getAttractionById(id: string): Promise<AttractionDetail | null> {
  const record = await fetchAttractionByUuid(id);
  return record ? toDetail(record) : null;
}

/**
 * False only when a timeslot product has no slot at all in the coming weeks —
 * the detail page then says so instead of offering a calendar with nothing in
 * it. `null` (unknown, or not a timeslot product) sells as normal.
 */
export async function isBookable(id: string): Promise<boolean | null> {
  return fetchBookable(id).catch(() => null);
}

/**
 * Bookable dates from the availability PREVIEW, plus the timeslots Bridgify
 * lists for each. BROWSING ONLY — the booking funnel reads its dates from the
 * cart's `dates` step, which is authoritative.
 *
 * The service labels this non-authoritative: the dates offered inside the cart
 * come from a live supplier check and can disagree. It is a browsing aid, and
 * a customer must never commit from it.
 *
 * Products with `availabilityType: 'BSN'` carry no availability data at all —
 * the service says so explicitly rather than returning an empty calendar that
 * reads as "fully booked", and this returns `[]` for them.
 */
export async function getAvailableDates(
  id: string,
  from: Date,
  days = 7,
): Promise<{ iso: string; price: number; times: string[] }[]> {
  const detail = await getAttractionById(id);
  const price = detail?.fromPrice ?? 0;

  const to = new Date(from);
  to.setUTCDate(to.getUTCDate() + days - 1);

  const availability = await fetchAvailability(id, isoDate(from), isoDate(to));
  if (!availability?.hasAvailabilityData) return [];

  return Object.entries(availability.dates)
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(0, days)
    .map(([iso, times]) => ({ iso, price, times: times ?? [] }));
}

function isoDate(d: Date): string {
  return [
    d.getUTCFullYear(),
    String(d.getUTCMonth() + 1).padStart(2, '0'),
    String(d.getUTCDate()).padStart(2, '0'),
  ].join('-');
}
